/**
 * TagTextInput - Converts a text input/textarea into a tag-style input.
 *
 * Values are stored as comma-separated strings in the hidden original field.
 * Tags can be added by typing and pressing Enter/comma, and removed by clicking ×.
 *
 * Uses existing CSS classes from _utilities_tags.scss.
 *
 * Usage:
 *   new TagTextInput(document.getElementById('id_keywords'));
 */
export class TagTextInput {
    constructor(element, options = {}) {
        this.original = element;
        this.separator = options.separator || ',';
        this.placeholder = options.placeholder || 'Type and press Enter';

        this.original.style.display = 'none';

        // Container
        this.container = document.createElement('div');
        this.container.className = 'tags-container';

        // Text input
        this.input = document.createElement('input');
        this.input.type = 'text';
        this.input.className = 'tag-input';
        this.input.placeholder = this.placeholder;
        this.container.appendChild(this.input);

        this.original.parentNode.insertBefore(this.container, this.original.nextSibling);

        // Parse existing value
        this._syncFromOriginal();

        // Events
        this.input.addEventListener('keydown', (e) => this._onKeydown(e));
        this.input.addEventListener('blur', () => this._commitInput());
        this.container.addEventListener('click', () => this.input.focus());
    }

    _syncFromOriginal() {
        const val = this.original.value.trim();
        if (!val) return;
        val.split(this.separator)
            .map(s => s.trim())
            .filter(Boolean)
            .forEach(tag => this._addTag(tag));
    }

    _onKeydown(e) {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            this._commitInput();
        } else if (e.key === 'Backspace' && !this.input.value) {
            // Remove last tag
            const tags = this.container.querySelectorAll('.tag-item');
            if (tags.length) {
                tags[tags.length - 1].remove();
                this._syncToOriginal();
            }
        }
    }

    _commitInput() {
        const val = this.input.value.trim().replace(/,+$/, '').trim();
        if (val) {
            this._addTag(val);
            this.input.value = '';
            this._syncToOriginal();
        }
    }

    _addTag(text) {
        const tag = document.createElement('span');
        tag.className = 'tag-item';
        tag.innerHTML = `<span>${this._escapeHtml(text)}</span><button type="button" class="tag-remove" title="Remove">&times;</button>`;
        tag.querySelector('.tag-remove').addEventListener('click', (e) => {
            e.stopPropagation();
            tag.remove();
            this._syncToOriginal();
        });
        this.container.insertBefore(tag, this.input);
    }

    _syncToOriginal() {
        const tags = Array.from(this.container.querySelectorAll('.tag-item span'))
            .map(el => el.textContent.trim());
        this.original.value = tags.join(', ');
        // Dispatch events so autosave and form change detection pick up the update
        this.original.dispatchEvent(new Event('input', { bubbles: true }));
        this.original.dispatchEvent(new Event('change', { bubbles: true }));
    }

    _escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
